/**
 * «Выписка банка» — §3 дополнения о сверке: загрузка выписки банка, список
 * загруженных и сразу под ними — что в выписке (§3.2). Повторная или
 * перекрывающаяся выписка платежи не задваивает — это решает сервер, а
 * экран показывает, сколько строк в файле оказались новыми.
 */

import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { rub } from '../../api';
import { Notice, isoDate, when } from '../../ui';
import { LIVE, plural, reconcileApi } from './api';
import type { Plan, Statement } from './api';
import { PlanView, planKey } from './Plan';
import { useOverview } from './Suppliers';

const statementsKey = ['reconcile', 'statements'] as const;

function rowsText(n: number): string {
  return `${n} ${plural(n, ['строка', 'строки', 'строк'])}`;
}

export function Statements() {
  const client = useQueryClient();
  const overview = useOverview();
  const list = useQuery<{ statements: Statement[] }, ApiError>({
    queryKey: statementsKey,
    queryFn: () => reconcileApi.statements(),
    ...LIVE,
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const send = useMutation<{ statement: Statement; plan: Plan }, ApiError, File>({
    mutationFn: (file) => reconcileApi.uploadStatement(file),
    onSuccess: (answer) => {
      const s = answer.statement;
      client.setQueryData(planKey, answer.plan);
      client.invalidateQueries({ queryKey: statementsKey });
      client.invalidateQueries({ queryKey: ['reconcile', 'overview'] });
      client.invalidateQueries({ queryKey: ['reconcile', 'ledger'] });
      setResult({
        ok: true,
        text:
          `Выписка за ${isoDate(s.period_from)}–${isoDate(s.period_to)} прочитана: ${rowsText(s.rows)}, ` +
          (s.new_rows === s.rows
            ? 'все новые.'
            : s.new_rows === 0
              ? 'новых нет — эти платежи уже были в прошлых выписках.'
              : `новых ${s.new_rows}, остальные уже были в прошлых выписках.`) +
          ' Ниже — что можно отметить оплаченным.',
      });
    },
    onError: (error) =>
      setResult({
        ok: false,
        text:
          error.detail ??
          'Выписка не загрузилась. Проверьте, что это Excel-файл из СберБизнеса, и попробуйте ещё раз.',
      }),
  });

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setResult(null);
    send.mutate(file);
  };

  const canEdit = overview.data?.can_edit ?? false;
  const suppliers = [...(overview.data?.suppliers ?? []), ...(overview.data?.immediate ?? [])]
    .map((s) => ({ id: s.id, name: s.name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <div className="rc-stack">
      <section className="rc-card rc-upload" aria-label="Загрузить выписку">
        <h2 className="rc-card__title">Выписка банка</h2>
        <p className="faint">
          Выписка СберБизнеса в Excel: «Выписка операций по лицевому счёту», любой период. Если периоды
          выписок перекрываются, платежи не задвоятся.
        </p>
        {overview.isPending ? null : canEdit ? (
          <div className="rc-actions">
            <button
              type="button"
              className="button button--primary"
              disabled={send.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {send.isPending ? 'Читаю выписку…' : 'Загрузить выписку банка'}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx"
              hidden
              onChange={pick}
              aria-label="Файл выписки банка"
            />
          </div>
        ) : (
          <p className="rc-note">
            Загружать выписку может тот, у кого право «Оплаты · поставщики и выписка банка» на правку. Попросите владельца.
          </p>
        )}
        {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      </section>

      <section className="rc-card" aria-label="Загруженные выписки">
        <h3 className="block-title">Загруженные выписки</h3>
        {list.isPending ? (
          <Loading />
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : list.data.statements.length === 0 ? (
          <p className="faint">
            Пока ни одной. Выгрузите в СберБизнесе выписку с 1 января и загрузите её выше — так система
            увидит все оплаты года.
          </p>
        ) : (
          <ul className="rc-plain-list rc-statements">
            {list.data.statements.map((s) => (
              <li key={s.id} className="rc-statement">
                <span className="rc-unused__main">
                  <strong>
                    {isoDate(s.period_from)} — {isoDate(s.period_to)}
                  </strong>
                  <span className="faint rc-purpose" title={s.file_name}>
                    {s.file_name}
                  </span>
                  <span className="faint">
                    {rowsText(s.rows)}, новых {s.new_rows} · загружена {when(s.uploaded_at)}
                    {s.uploaded_by ? `, ${s.uploaded_by}` : ''}
                  </span>
                </span>
                <span className="num" title="Списания по выписке">
                  {rub(s.debit_total)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <PlanView canEdit={canEdit} suppliers={suppliers} />
    </div>
  );
}
